import { useState, useCallback } from "react";
import { API } from "aws-amplify";
import { UPMSTransformer, UPMSMessageType } from "../utils/UPMSTransformer";

/**
 * UPMS ingestion response
 */
export interface UPMSIngestionResponse {
    [key: string]: any;
}

/**
 * Hook to manage UPMS ingestion operations
 *
 * Handles:
 * - Ingesting NewRx prescriptions to UPMS
 * - Ingesting TransferRx prescriptions to UPMS
 * - Loading and error states
 * - Response data management
 */
export const useUPMSIngestion = () => {
    const [isIngesting, setIsIngesting] = useState(false);
    const [upmsResponse, setUpmsResponse] = useState<UPMSIngestionResponse | null>(null);
    const [transformedPayload, setTransformedPayload] = useState<any>(null);
    const [error, setError] = useState<string>("");

    /**
     * Ingest NewRx prescription to UPMS
     */
    const ingestNewRx = useCallback(async (
        prescriptionData: any,
        fileName: string
    ): Promise<UPMSIngestionResponse> => {
        setIsIngesting(true);
        setError("");

        try {
            const apiName = "ApaqPortalAPI";
            const path = "/prescription/ingest-new-prescription-message";

            // Parse rxPayload if it's a string
            let parsedPrescriptionData = prescriptionData;
            if (
                prescriptionData.rxPayload &&
                typeof prescriptionData.rxPayload === "string"
            ) {
                try {
                    parsedPrescriptionData = JSON.parse(prescriptionData.rxPayload);
                } catch (error) {
                    throw new Error("Invalid prescription data format");
                }
            }

            // Transform prescription data to UPMS format
            const upmsPayload = UPMSTransformer.transformToUPMS(
                parsedPrescriptionData,
                fileName,
                UPMSMessageType.NEW_RX
            );

            setTransformedPayload(upmsPayload);

            const response = await API.post(apiName, path, { body: upmsPayload });

            setUpmsResponse(response);
            setIsIngesting(false);
            return response;
        } catch (err) {
            const error = err as any;
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to ingest NewRx to UPMS";

            console.error("Error ingesting NewRx to UPMS:", error);
            setError(errorMessage);
            setIsIngesting(false);
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Ingest TransferRx prescription to UPMS
     */
    const ingestTransferRx = useCallback(async (
        prescriptionData: any,
        fileName: string
    ): Promise<UPMSIngestionResponse> => {
        setIsIngesting(true);
        setError("");

        try {
            const apiName = "ApaqPortalAPI";
            const path = "/prescription/ingest-transfer-in-prescription-message";

            // Transform prescription data to UPMS format
            const upmsPayload = UPMSTransformer.transformToUPMS(
                prescriptionData,
                fileName,
                UPMSMessageType.TRANSFER_RX
            );

            setTransformedPayload(upmsPayload);

            const response = await API.post(apiName, path, { body: upmsPayload });

            setUpmsResponse(response);
            setIsIngesting(false);
            return response;
        } catch (err) {
            const error = err as any;
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to ingest TransferRx to UPMS";

            console.error("Error ingesting TransferRx to UPMS:", error);
            setError(errorMessage);
            setIsIngesting(false);
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Ingest RxRenewalResponse prescription to UPMS
     */
    const ingestRenewalRx = useCallback(async (
        prescriptionData: any,
        fileName: string
    ): Promise<UPMSIngestionResponse> => {
        setIsIngesting(true);
        setError("");

        try {
            const apiName = "ApaqPortalAPI";
            const path = "/prescription/ingest-renewal-prescription-message";

            // Transform prescription data to UPMS format
            const upmsPayload = UPMSTransformer.transformToUPMS(
                prescriptionData,
                fileName,
                UPMSMessageType.RENEWAL_RX
            );

            setTransformedPayload(upmsPayload);

            const response = await API.post(apiName, path, { body: upmsPayload });

            setUpmsResponse(response);
            setIsIngesting(false);
            return response;
        } catch (err) {
            const error = err as any;
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to ingest Renewal to UPMS";

            console.error("Error ingesting Renewal to UPMS:", error);
            setError(errorMessage);
            setIsIngesting(false);
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Reset the hook state
     */
    const reset = useCallback(() => {
        setIsIngesting(false);
        setUpmsResponse(null);
        setTransformedPayload(null);
        setError("");
    }, []);

    return {
        isIngesting,
        upmsResponse,
        transformedPayload,
        error,
        ingestNewRx,
        ingestTransferRx,
        ingestRenewalRx,
        reset,
    };
};
